import { useMemo, useState } from 'react';
import {
  Box,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { NumericTextField } from '../../../../components/forms/NumericTextField';
import type { CercharGeneral, CercharStylus } from '../../../../types/rockCerchar';
import { DerivedValue, fmt, inputSx, paperSx, SectionHeader } from '../../rock-tcs/sectionStyles';
import { FormulaHeaderTooltip } from '../../components/FormulaHeaderTooltip';
import { useTestFormulas } from '../../../../hooks/useTestFormulas';
import {
  CERCHAR_DIRECTIONS,
  CERCHAR_PLANES_OF_WEAKNESS,
} from '../rockCercharFields';
import { computeCercharPreview, perStylusAverage } from '../computations';
import { CaiClassificationGuide } from './CaiClassificationGuide';

interface Props {
  general: CercharGeneral;
  styluses: CercharStylus[];
  onStylusChange: (
    stylusNumber: number,
    field: keyof CercharStylus,
    value: CercharStylus[keyof CercharStylus],
  ) => void;
  readOnly?: boolean;
}

const headSx = { py: 0.5, px: 1, fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary' } as const;
const cellSx = { py: 0.4, px: 1, fontSize: '0.76rem' } as const;
const ADD_OTHER = '__add_other__';

/**
 * Per-sample stylus entry table (item 4.6 / 4.8 — the report-faithful core).
 * One row per stylus 1–5: two measured diameters, a computed read-only average,
 * and the per-stylus Direction-to-Plane + Plane-of-Weakness dropdowns. Below it,
 * a live RESULTS block recomputed via computations.ts as the operator types.
 *
 * The report and data model are PER-STYLUS (two diameters + direction + plane);
 * the batch grid (CercharResultGrid) shows the rolled-up per-sample summary that
 * matches the Result Entry.png mockup, and embeds THIS table as the expandable
 * row detail for the current sample (master–detail, PLT-direction). Detailed
 * entry lives here on the current sample only (R4/R5).
 */
export const StylusEntryTable = ({ general, styluses, onStylusChange, readOnly }: Props) => {
  // Plane of Weakness is fixed-but-editable (item 4.8): keep any free-text values
  // the operator added so they survive a re-render alongside the canonical list.
  const [extraPlanes, setExtraPlanes] = useState<string[]>([]);

  // Formula-registry tooltips for the computed cells (FEAT "Formula Registry").
  const { formulaFor } = useTestFormulas('cerchar');
  const perStylusF = formulaFor('perStylusAverage');

  const planeOptions = useMemo(() => {
    const stored = styluses
      .map((s) => s.planeOfWeakness)
      .filter((v): v is string => !!v && !CERCHAR_PLANES_OF_WEAKNESS.includes(v as never));
    return Array.from(new Set([...CERCHAR_PLANES_OF_WEAKNESS, ...extraPlanes, ...stored]));
  }, [styluses, extraPlanes]);

  const preview = useMemo(
    () => computeCercharPreview(
      styluses.map((s) => ({ diameter1Mm: s.diameter1Mm, diameter2Mm: s.diameter2Mm })),
      general.testSurfaceCondition,
    ),
    [styluses, general.testSurfaceCondition],
  );

  const handlePlaneChange = (stylusNumber: number, raw: string) => {
    if (readOnly) return;
    if (raw === ADD_OTHER) {
      // eslint-disable-next-line no-alert
      const custom = window.prompt('Plane of Weakness (free text):')?.trim();
      if (!custom) return;
      if (!planeOptions.includes(custom)) setExtraPlanes((prev) => [...prev, custom]);
      onStylusChange(stylusNumber, 'planeOfWeakness', custom);
      return;
    }
    onStylusChange(stylusNumber, 'planeOfWeakness', raw || null);
  };

  return (
    <Paper variant="outlined" sx={paperSx}>
      <SectionHeader label="Stylus Tip Wear Flat Diameters" />
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={headSx}>Stylus No.</TableCell>
            <TableCell sx={headSx} align="right">Diameter 1 (mm)</TableCell>
            <TableCell sx={headSx} align="right">Diameter 2 (mm)</TableCell>
            <TableCell sx={headSx} align="right">
              {perStylusF?.formula ? (
                <FormulaHeaderTooltip label="Average Diameter (mm)" formula={perStylusF.formula} note={perStylusF.note}>
                  <span style={{ borderBottom: '1px dotted', cursor: 'help' }}>Average Diameter (mm)</span>
                </FormulaHeaderTooltip>
              ) : (
                'Average Diameter (mm)'
              )}
            </TableCell>
            <TableCell sx={headSx}>Direction to Plane of Weakness</TableCell>
            <TableCell sx={headSx}>Plane of Weakness</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {styluses.map((s) => {
            const avg = perStylusAverage(s.diameter1Mm, s.diameter2Mm);
            return (
              <TableRow key={s.stylusNumber}>
                <TableCell sx={cellSx}>{s.stylusNumber}</TableCell>
                <TableCell sx={cellSx} align="right">
                  <NumericTextField
                    size="small"
                    value={s.diameter1Mm}
                    onChange={(v) => onStylusChange(s.stylusNumber, 'diameter1Mm', v)}
                    InputProps={{ readOnly }}
                    sx={inputSx}
                  />
                </TableCell>
                <TableCell sx={cellSx} align="right">
                  <NumericTextField
                    size="small"
                    value={s.diameter2Mm}
                    onChange={(v) => onStylusChange(s.stylusNumber, 'diameter2Mm', v)}
                    InputProps={{ readOnly }}
                    sx={inputSx}
                  />
                </TableCell>
                <TableCell sx={{ ...cellSx, fontWeight: 600 }} align="right">{fmt(avg, 3)}</TableCell>
                <TableCell sx={cellSx}>
                  <TextField
                    select size="small" fullWidth
                    value={s.directionToPlane ?? ''}
                    onChange={(e) => onStylusChange(s.stylusNumber, 'directionToPlane', e.target.value || null)}
                    InputProps={{ readOnly }}
                    sx={inputSx}
                  >
                    {CERCHAR_DIRECTIONS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                  </TextField>
                </TableCell>
                <TableCell sx={cellSx}>
                  <TextField
                    select size="small" fullWidth
                    value={s.planeOfWeakness ?? ''}
                    onChange={(e) => handlePlaneChange(s.stylusNumber, e.target.value)}
                    InputProps={{ readOnly }}
                    sx={inputSx}
                  >
                    {planeOptions.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                    {!readOnly && (
                      <MenuItem value={ADD_OTHER} sx={{ fontStyle: 'italic' }}>Add other…</MenuItem>
                    )}
                  </TextField>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* ── Live RESULTS block (item 4.6) — recomputed as the operator types ── */}
      <SectionHeader label="Results (auto-calculated)" />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.25 }}>
        <DerivedValue
          label="Average Wear Flat Diameter"
          value={fmt(preview.avgWearFlatDiaMm, 3)}
          suffix="mm"
          formula={formulaFor('ComputedAvgWearFlatDiaMm')?.formula}
          note={formulaFor('ComputedAvgWearFlatDiaMm')?.note}
        />
        {/* CAIs is N/A for a freshly-broken surface — show "-" like the report. */}
        <DerivedValue
          label="Saw Cut CAI (CAIs)"
          value={preview.caisSawCut === null ? '-' : fmt(preview.caisSawCut, 2)}
          formula={formulaFor('ComputedCAIs')?.formula}
          note={formulaFor('ComputedCAIs')?.note}
        />
        <DerivedValue
          label="CAI"
          value={fmt(preview.cai, 2)}
          formula={formulaFor('ComputedCAI')?.formula}
          note={formulaFor('ComputedCAI')?.note}
        />
        {/* CAI Classification is a string output — intentionally not registered.
            BUG-2026-0142 rework — the "View Classification Guide" popup sits
            beside it (re-added per SHegger's request). */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
          <DerivedValue label="CAI Classification" value={preview.classification ?? '—'} />
          <CaiClassificationGuide />
        </Box>
      </Box>
    </Paper>
  );
};
